import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Users,
  Eye,
  Clock,
  MapPin,
  RefreshCw,
  Globe,
  Monitor,
  LogOut,
  TrendingUp,
  Calendar,
} from "lucide-react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";

interface VisitorSession {
  id: string;
  visitor_id: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  pages_viewed: number;
  is_returning: boolean;
  visit_count: number;
  source: string;
  city: string | null;
  country: string | null;
  device_type: string;
}

interface PageView {
  id: string;
  session_id: string;
  page_path: string;
  page_title: string | null;
  viewed_at: string;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [sessions, setSessions] = useState<VisitorSession[]>([]);
  const [pageViews, setPageViews] = useState<PageView[]>([]);
  const [dateRange, setDateRange] = useState(7);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || session.user.email !== "moein@udaadu.com") {
        navigate("/admin-login");
        return;
      }
      fetchData();
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session || session.user?.email !== "moein@udaadu.com") {
        navigate("/admin-login");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, dateRange]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const startDate = startOfDay(subDays(new Date(), dateRange)).toISOString();
      const endDate = endOfDay(new Date()).toISOString();

      const [sessionsRes, pageViewsRes] = await Promise.all([
        supabase
          .from("visitor_sessions")
          .select("*")
          .gte("started_at", startDate)
          .lte("started_at", endDate)
          .order("started_at", { ascending: false }),
        supabase
          .from("page_views")
          .select("*")
          .gte("viewed_at", startDate)
          .lte("viewed_at", endDate)
          .order("viewed_at", { ascending: false }),
      ]);

      if (sessionsRes.error) throw sessionsRes.error;
      if (pageViewsRes.error) throw pageViewsRes.error;

      setSessions(sessionsRes.data || []);
      setPageViews(pageViewsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error loading data",
        description: "Please try refreshing the page.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin-login");
  };

  // Calculate stats
  const totalVisitors = new Set(sessions.map((s) => s.visitor_id)).size;
  const totalPageViews = pageViews.length;
  const returningVisitors = sessions.filter((s) => s.is_returning).length;
  const avgDuration = sessions.length
    ? Math.round(
        sessions.reduce((acc, s) => acc + (s.duration_seconds || 0), 0) / sessions.length
      )
    : 0;

  const sourceStats = sessions.reduce((acc, s) => {
    acc[s.source] = (acc[s.source] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const deviceStats = sessions.reduce((acc, s) => {
    const device = s.device_type || "unknown";
    acc[device] = (acc[device] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const pageStats = pageViews.reduce((acc, pv) => {
    acc[pv.page_path] = (acc[pv.page_path] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const sessionPageViews = selectedSession
    ? pageViews.filter((pv) => pv.session_id === selectedSession)
    : [];

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "0s";
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const cardStyle = {
    backgroundColor: "#0a0a0a",
    boxShadow: "8px 8px 16px #000000, -8px -8px 16px #141414",
  };

  const insetStyle = {
    backgroundColor: "#0a0a0a",
    boxShadow: "inset 4px 4px 8px #000000, inset -4px -4px 8px #141414",
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#E4683B]">Analytics Dashboard</h1>
          <p className="text-gray-400">UDA ADU Visitor Insights</p>
        </div>
        <div className="flex gap-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(parseInt(e.target.value))}
            className="px-4 py-2 rounded-xl bg-[#141414] text-white border-0 focus:outline-none focus:ring-2 focus:ring-[#E4683B]"
            style={insetStyle}
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchData}
            className="p-2 rounded-xl text-[#E4683B] transition-all"
            style={cardStyle}
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={handleLogout}
            className="px-4 py-2 rounded-xl text-white flex items-center gap-2 transition-all"
            style={{ ...cardStyle, backgroundColor: "#E4683B" }}
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="p-6 rounded-2xl" style={cardStyle}>
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6 text-[#E4683B]" />
            <span className="text-gray-400">Unique Visitors</span>
          </div>
          <p className="text-3xl font-bold">{totalVisitors}</p>
        </div>
        <div className="p-6 rounded-2xl" style={cardStyle}>
          <div className="flex items-center gap-3 mb-2">
            <Eye className="w-6 h-6 text-[#E4683B]" />
            <span className="text-gray-400">Page Views</span>
          </div>
          <p className="text-3xl font-bold">{totalPageViews}</p>
        </div>
        <div className="p-6 rounded-2xl" style={cardStyle}>
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-6 h-6 text-[#E4683B]" />
            <span className="text-gray-400">Returning</span>
          </div>
          <p className="text-3xl font-bold">{returningVisitors}</p>
        </div>
        <div className="p-6 rounded-2xl" style={cardStyle}>
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-6 h-6 text-[#E4683B]" />
            <span className="text-gray-400">Avg Duration</span>
          </div>
          <p className="text-3xl font-bold">{formatDuration(avgDuration)}</p>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Sources */}
        <div className="p-6 rounded-2xl" style={cardStyle}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#E4683B]" /> Traffic Sources
          </h3>
          <div className="space-y-3">
            {Object.entries(sourceStats)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([source, count]) => (
                <div key={source} className="flex justify-between items-center">
                  <span className="text-gray-300">{source}</span>
                  <span className="text-[#E4683B] font-semibold">{count}</span>
                </div>
              ))}
            {Object.keys(sourceStats).length === 0 && (
              <p className="text-gray-500">No data yet</p>
            )}
          </div>
        </div>

        {/* Devices */}
        <div className="p-6 rounded-2xl" style={cardStyle}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-[#E4683B]" /> Devices
          </h3>
          <div className="space-y-3">
            {Object.entries(deviceStats)
              .sort((a, b) => b[1] - a[1])
              .map(([device, count]) => (
                <div key={device} className="flex justify-between items-center">
                  <span className="text-gray-300 capitalize">{device}</span>
                  <span className="text-[#E4683B] font-semibold">{count}</span>
                </div>
              ))}
            {Object.keys(deviceStats).length === 0 && (
              <p className="text-gray-500">No data yet</p>
            )}
          </div>
        </div>

        {/* Top Pages */}
        <div className="p-6 rounded-2xl" style={cardStyle}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#E4683B]" /> Top Pages
          </h3>
          <div className="space-y-3">
            {Object.entries(pageStats)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([page, count]) => (
                <div key={page} className="flex justify-between items-center">
                  <span className="text-gray-300 truncate max-w-[150px]">{page}</span>
                  <span className="text-[#E4683B] font-semibold">{count}</span>
                </div>
              ))}
            {Object.keys(pageStats).length === 0 && (
              <p className="text-gray-500">No data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Visitor Sessions Table */}
      <div className="rounded-2xl p-6" style={cardStyle}>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#E4683B]" /> Recent Visitor Sessions
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-3 px-4 text-gray-400 font-medium">Visitor</th>
                <th className="py-3 px-4 text-gray-400 font-medium">Date</th>
                <th className="py-3 px-4 text-gray-400 font-medium">Duration</th>
                <th className="py-3 px-4 text-gray-400 font-medium">Pages</th>
                <th className="py-3 px-4 text-gray-400 font-medium">Source</th>
                <th className="py-3 px-4 text-gray-400 font-medium">Device</th>
                <th className="py-3 px-4 text-gray-400 font-medium">Returning</th>
              </tr>
            </thead>
            <tbody>
              {sessions.slice(0, 20).map((session) => (
                <tr
                  key={session.id}
                  onClick={() =>
                    setSelectedSession(
                      selectedSession === session.id ? null : session.id
                    )
                  }
                  className={`border-b border-gray-800/50 cursor-pointer transition-colors ${
                    selectedSession === session.id
                      ? "bg-[#141414]"
                      : "hover:bg-[#0d0d0d]"
                  }`}
                >
                  <td className="py-3 px-4 text-sm">
                    <span className="text-gray-300">
                      {session.visitor_id.substring(0, 12)}...
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-300">
                    {format(new Date(session.started_at), "MMM d, h:mm a")}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-300">
                    {formatDuration(session.duration_seconds)}
                  </td>
                  <td className="py-3 px-4 text-sm text-[#E4683B]">
                    {session.pages_viewed}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-300">
                    {session.source}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-300 capitalize">
                    {session.device_type || "—"}
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {session.is_returning ? (
                      <span className="text-green-400">Yes ({session.visit_count}x)</span>
                    ) : (
                      <span className="text-gray-500">No</span>
                    )}
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    No visitor data yet. Visitors will appear here as they browse your site.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Selected Session Details */}
        {selectedSession && sessionPageViews.length > 0 && (
          <div className="mt-6 p-4 rounded-xl" style={insetStyle}>
            <h4 className="text-sm font-semibold text-[#E4683B] mb-3">
              Pages Viewed in This Session
            </h4>
            <div className="space-y-2">
              {sessionPageViews.map((pv) => (
                <div key={pv.id} className="flex justify-between text-sm">
                  <span className="text-gray-300">{pv.page_path}</span>
                  <span className="text-gray-500">
                    {format(new Date(pv.viewed_at), "h:mm:ss a")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
